import { createFileRoute, useMatches, useNavigate, useRouter, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import {
  getCampaignServer,
  getSavedCampaignICPServer,
  listCampaignOutgoingTasksServer,
  listCampaignMessagesServer,
  listCampaignLeadsDetailServer,
  listCampaignQuotesServer,
  listCampaignSuppliersServer,
  createAutomationTaskServer,
  type CampaignSupplierListResponse,
} from "@/server/campaigns";
import { getCampaignStatisticsServer } from "@/server/campaign-statistics";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { Overview } from "@/components/campaign/detail/overview";
import { resumeCampaignServer, pauseCampaignServer } from "@/server/campaigns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatsHeader } from "@/components/campaign/detail/stats-header";
import { ClipboardCheck, LayoutGrid, Table, Mail, FileText, FileQuestion } from "lucide-react";
import ActivityPage from "@/components/campaign/detail/activity";
import MessagePage from "@/components/campaign/detail/messages-table";
import LeadsPage from "@/components/campaign/detail/leads-table";
import SuppliersPage from "@/components/campaign/detail/suppliers-table";
import QuotesPage from "@/components/campaign/detail/quotes-table";
import TasksPage from "@/components/campaign/detail/tasks-table";
import { SupplierQuestionTodos } from "@/components/campaign/detail/supplier-question-todos";
import { OrderBy } from "@/lib/api/generated";
import { toast } from "sonner";
import { logError } from "@/lib/structured-log";
import { extractApiErrorMessage, isAutomationTaskLimitError } from "@/lib/api/error";
import { listCampaignActivitiesServer } from "@/server/activities";
import { InlineSvg } from "@/components/ui/inline-svg";
import {
  RfqSettingSidebar,
  type RfqSettingConfirmPayload,
} from "@/components/campaign/rfq-setting-sidebar";
import { CampaignSettingsSidebar } from "@/components/campaign/campaign-settings-sidebar";
import { UpgradePlanModal } from "@/components/billing/UpgradePlanModal";
import {
  resolveRfqDefaultCountryFromLocation,
  type RfqConfirmCard,
  type RfqConfirmFieldErrors,
} from "@/components/campaign/rfq-confirm-card";
import activitySvg from "@/assets/icons/activity.svg?raw";
import { getWorkspaceCompanyProfileServer } from "@/server/workspace-company-profile";
import { normalizeIcpCriteria } from "@/lib/icp-criteria";
import {
  deriveCriteriaFromSupplierItems,
  getCampaignDetailEditSidebar,
  toCommitSupplierInput,
  toSupplierListCardItem,
} from "@/components/campaign/detail/edit-flow-utils";
import { getAutomationTaskLimitDisplayMessage } from "@/components/campaign/task-limit-error-message";

export const Route = createFileRoute("/workspace/$workspaceSlug/campaigns/$campaignId/")({
  // Cache campaign detail for 10 seconds (read-only page)
  // Medium cache since this is a detail view that doesn't change frequently
  staleTime: 10_000, // 10 seconds
  gcTime: 3 * 60_000, // 3 minutes until garbage collection
  loader: async ({ params, context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "campaign.detail",
      parentMatchPromise,
    );

    // 获取 campaign 详情
    const campaign = await getCampaignServer({
      data: {
        workspaceId,
        campaignId: params.campaignId,
      },
    });

    // 统计数据：默认按最近 90 天
    const today = new Date();
    const endDate = today.toISOString();
    const startDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();

    const commonParams = { page: 1, page_size: 20, orderBy: OrderBy.desc };
    const isSourcingCampaign = campaign.agent_mode === "sourcing";

    const detailListPromise = isSourcingCampaign
      ? listCampaignSuppliersServer({
          data: {
            workspaceId,
            campaignId: params.campaignId,
          },
        })
      : listCampaignLeadsDetailServer({
          data: {
            workspaceId,
            campaignId: params.campaignId,
            params: commonParams,
          },
        });

    const quotesPromise = isSourcingCampaign
      ? listCampaignQuotesServer({
          data: {
            workspaceId,
            campaignId: params.campaignId,
          },
        })
      : Promise.resolve(null);

    const outgoingTasksPromise = isSourcingCampaign
      ? Promise.resolve(null)
      : listCampaignOutgoingTasksServer({
          data: {
            workspaceId,
            campaignId: params.campaignId,
            page: 1,
            pageSize: 20,
          },
        });
    const companyProfilePromise = isSourcingCampaign
      ? getWorkspaceCompanyProfileServer({ data: { workspaceId } }).catch(() => null)
      : Promise.resolve(null);
    const savedIcpPromise = !isSourcingCampaign
      ? getSavedCampaignICPServer({ data: { workspaceId, campaignId: params.campaignId } }).catch(
          () => null,
        )
      : Promise.resolve(null);

    // 并发请求统计数据、消息列表与当前 agent_mode 对应详情列表
    const [
      statistics,
      messages,
      detailList,
      activities,
      quotes,
      outgoingTasks,
      companyProfile,
      savedIcp,
    ] = await Promise.all([
      getCampaignStatisticsServer({
        data: {
          workspaceId,
          campaignId: params.campaignId,
          params: { start_date: startDate, end_date: endDate },
        },
      }),
      listCampaignMessagesServer({
        data: {
          workspaceId,
          campaignId: params.campaignId,
          params: commonParams,
        },
      }),
      detailListPromise,
      listCampaignActivitiesServer({
        data: {
          workspaceId,
          campaignId: campaign.id,
          params: commonParams,
        },
      }),
      quotesPromise,
      outgoingTasksPromise,
      companyProfilePromise,
      savedIcpPromise,
    ]);

    return {
      campaign,
      workspaceId,
      statistics,
      messages,
      leads: isSourcingCampaign ? null : detailList,
      suppliers: isSourcingCampaign ? detailList : null,
      quotes,
      outgoingTasks,
      activities,
      workspaceCompanyLocation: companyProfile?.location ?? null,
      savedIcp,
      defaultDateRange: { start: startDate, end: endDate },
    };
  },
  component: CampaignDetailPage,
});

function CampaignDetailPage() {
  const { t } = useTranslation(["pages", "common"]);
  const router = useRouter();
  const navigate = useNavigate();
  const matches = useMatches();
  const workspaceMatch = matches.find((match) => match.routeId === "/workspace/$workspaceSlug");
  const workspaceData = workspaceMatch?.loaderData as
    | { userDisplayName?: string | null }
    | undefined;
  const currentUserDisplayName = workspaceData?.userDisplayName ?? null;
  const { campaign, workspaceId, quotes, suppliers, workspaceCompanyLocation, savedIcp } =
    Route.useLoaderData();
  const { workspaceSlug } = Route.useParams();
  const isSourcingCampaign = campaign.agent_mode === "sourcing";
  const supplierList = isSourcingCampaign
    ? (suppliers as CampaignSupplierListResponse | null)
    : null;
  const supplierItems = useMemo(() => supplierList?.items ?? [], [supplierList]);
  const supplierSidebarItems = useMemo(
    () => supplierItems.map(toSupplierListCardItem),
    [supplierItems],
  );
  const supplierSearchText = supplierItems[0]?.search_text ?? "";
  const supplierCriteria = useMemo(
    () => deriveCriteriaFromSupplierItems(supplierItems),
    [supplierItems],
  );
  const savedIcpCriteria = useMemo(
    () => normalizeIcpCriteria(savedIcp?.criteria, "icp"),
    [savedIcp?.criteria],
  );
  const defaultSalesTargetProfile = useMemo(
    () => ({
      searchText: savedIcp?.search_query ?? campaign.name ?? "",
      criteria: savedIcpCriteria,
    }),
    [campaign.name, savedIcp?.search_query, savedIcpCriteria],
  );

  interface CampaignDetailRefreshKeys {
    overview?: number;
    activity: number;
    lead: number;
    qualifiedLeads: number;
    message: number;
    supplier: number;
  }

  // Tab state to allow switching from child components like Overview
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [tabRefreshKeys, setTabRefreshKeys] = useState<CampaignDetailRefreshKeys>({
    activity: 0,
    lead: 0,
    qualifiedLeads: 0,
    message: 0,
    supplier: 0,
  });
  const [isCampaignStatusUpdating, setIsCampaignStatusUpdating] = useState(false);
  const [showRfqSettingSidebar, setShowRfqSettingSidebar] = useState(false);
  const [showSalesSettingSidebar, setShowSalesSettingSidebar] = useState(false);
  const [taskLimitMessage, setTaskLimitMessage] = useState<string | null>(null);
  const [isRfqCommitting, setIsRfqCommitting] = useState(false);
  const [rfqFieldErrors, setRfqFieldErrors] = useState<RfqConfirmFieldErrors>({});
  const rfqSidebarDetails = useMemo<RfqConfirmCard>(
    () => ({
      product: quotes?.inquiry_product?.search_query ?? campaign.name ?? "",
      qualityRequirements: quotes?.inquiry_product?.criteria ?? [],
      notes: "",
    }),
    [campaign.name, quotes?.inquiry_product?.criteria, quotes?.inquiry_product?.search_query],
  );

  // Only for this page: active tab underline overlapping Separator
  const activeUnderlineClass =
    "relative after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-[-14px] after:h-px after:bg-gray-900 after:opacity-0 data-[state=active]:after:opacity-100";

  const handleTabChange = (nextTab: string) => {
    setActiveTab(nextTab);

    if (nextTab === "overview") {
      setTabRefreshKeys((current) => ({ ...current, overview: (current.overview ?? 0) + 1 }));
      return;
    }

    if (nextTab === "activity") {
      setTabRefreshKeys((current) => ({ ...current, activity: current.activity + 1 }));
      return;
    }

    if (nextTab === "lead") {
      setTabRefreshKeys((current) => ({ ...current, lead: current.lead + 1 }));
      return;
    }

    if (nextTab === "qualified-leads") {
      setTabRefreshKeys((current) => ({
        ...current,
        qualifiedLeads: current.qualifiedLeads + 1,
      }));
      return;
    }

    if (nextTab === "message") {
      setTabRefreshKeys((current) => ({ ...current, message: current.message + 1 }));
      return;
    }

    if (nextTab === "supplier") {
      setTabRefreshKeys((current) => ({ ...current, supplier: current.supplier + 1 }));
    }
  };

  const handleEdit = () => {
    const sidebar = getCampaignDetailEditSidebar(campaign.agent_mode);
    if (sidebar === "rfq-settings") {
      setShowRfqSettingSidebar(true);
      return;
    }

    setShowSalesSettingSidebar(true);
  };

  const handleRfqConfirm = async ({
    rfq,
    setting,
    supplierProfile,
  }: RfqSettingConfirmPayload): Promise<void> => {
    if (isRfqCommitting) {
      return;
    }
    if (rfq.requirements.trim().length === 0) {
      setRfqFieldErrors({ requirements: true });
      toast.error(t("pages:campaigns.sourcingChat.rfqCard.requirementsRequired"));
      return;
    }
    if (!setting.sender_accounts || setting.sender_accounts.length === 0) {
      toast.error(t("pages:campaigns.settingForm.validation.accountRequired"));
      return;
    }

    setRfqFieldErrors({});
    setIsRfqCommitting(true);
    try {
      const result = await createAutomationTaskServer({
        data: {
          workspaceId,
          sourceCampaignId: campaign.id,
          campaignName: setting.formValues.campaign_name ?? "",
          chatSessionId: campaign.chat_session_id,
          rfq: {
            product: rfq.product,
            requirements: rfq.requirements,
            order_quantity: rfq.orderQuantity,
            country: rfq.country,
            delivery_time: rfq.deliveryTime,
          },
          setting: {
            sender_name: setting.formValues.sender_name,
            sender_job_title: setting.formValues.sender_job_title,
            sender_accounts: setting.sender_accounts,
            campaign_name: setting.formValues.campaign_name,
            knowledge_attachment_ids: setting.formValues.knowledge_attachment_ids,
            language: setting.formValues.language,
            timezone: setting.formValues.timezone,
          },
          suppliers: supplierSidebarItems.map(toCommitSupplierInput),
          supplierSearchText: supplierProfile.searchText || supplierSearchText || rfq.product,
        },
      });

      toast.success("Automation task created.");
      setShowRfqSettingSidebar(false);
      await navigate({
        to: "/workspace/$workspaceSlug/campaigns/$campaignId/launch-success",
        params: { workspaceSlug, campaignId: result.campaign.id },
      });
    } catch (error) {
      const message = isAutomationTaskLimitError(error)
        ? getAutomationTaskLimitDisplayMessage(error, t)
        : extractApiErrorMessage(error, t("pages:campaigns.createFailed"));
      logError("campaigns.detail.automation_task.create_failed", {
        workspace_id: workspaceId,
        campaign_id: campaign.id,
        error: message,
      });
      toast.error(message);
      if (isAutomationTaskLimitError(error)) {
        setTaskLimitMessage(message);
      }
    } finally {
      setIsRfqCommitting(false);
    }
  };

  const handleResume = async () => {
    if (isCampaignStatusUpdating) return;
    setIsCampaignStatusUpdating(true);
    try {
      await resumeCampaignServer({
        data: { workspaceId, campaignId: campaign.id },
      });
      toast.success(t("pages:campaigns.toasts.resumeSuccess"));
      await router.invalidate();
    } catch (error) {
      logError("pause resume failed", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      toast.error(t("pages:campaigns.toasts.resumeFailed"));
    } finally {
      setIsCampaignStatusUpdating(false);
    }
  };

  const handlePause = async () => {
    if (isCampaignStatusUpdating) return;
    setIsCampaignStatusUpdating(true);
    try {
      await pauseCampaignServer({
        data: { workspaceId, campaignId: campaign.id },
      });
      toast.success(t("pages:campaigns.toasts.pauseSuccess"));
      await router.invalidate();
    } catch (error) {
      logError("pause campaign failed", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      toast.error(t("pages:campaigns.toasts.pauseFailed"));
    } finally {
      setIsCampaignStatusUpdating(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-white">
      <div className="flex min-w-0 flex-1 flex-col bg-white">
        <div className="h-[64px] px-6">
          <div className="flex h-full items-center justify-between">
            <div className="flex items-center gap-2 text-base text-gray-500">
              <Link
                to="/workspace/$workspaceSlug/campaigns"
                params={{ workspaceSlug }}
                className="hover:text-gray-900"
                preload="intent"
              >
                {t("pages:campaigns.title")}
              </Link>
              <span>/</span>
              <span className="font-semibold text-gray-900">{campaign.name}</span>
            </div>
          </div>
        </div>
        <Separator className="bg-[#dfdfdf]" />

        <div className="space-y-4 px-6 py-4">
          <StatsHeader
            title={campaign.name ?? t("pages:campaigns.detail.header.untitled")}
            subtitle={t("pages:campaigns.detail.header.subtitle")}
            agentMode={campaign.agent_mode}
            isPaused={campaign.status === "paused"}
            isActive={campaign.status === "active"}
            onResume={handleResume}
            onPause={handlePause}
            onEdit={handleEdit}
            statusActionLoading={isCampaignStatusUpdating}
          />
        </div>

        <Separator className="bg-[#dfdfdf]" />

        {/* Content with Header + Tabs */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            {/** Controlled tabs to allow programmatic switching from Overview */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
              <TabsList className="bg-transparent mt-[9px] mb-[9px] ml-4">
                <TabsTrigger
                  value="overview"
                  className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                >
                  <LayoutGrid className="mr-2 size-4" /> {t("pages:campaigns.tabs.overview")}
                </TabsTrigger>
                <Separator
                  orientation="vertical"
                  className="mx-4 data-[orientation=vertical]:h-[20px]"
                />
                <TabsTrigger
                  value="activity"
                  className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                >
                  <InlineSvg content={activitySvg} /> {t("pages:campaigns.tabs.activity")}
                </TabsTrigger>
                <Separator
                  orientation="vertical"
                  className="mx-4 data-[orientation=vertical]:h-[20px]"
                />
                <TabsTrigger
                  value={isSourcingCampaign ? "supplier" : "lead"}
                  className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                >
                  <Table className="mr-2 size-4" />{" "}
                  {t(
                    isSourcingCampaign
                      ? "pages:campaigns.tabs.supplier"
                      : "pages:campaigns.tabs.lead",
                  )}
                </TabsTrigger>
                {!isSourcingCampaign ? (
                  <>
                    <Separator
                      orientation="vertical"
                      className="mx-4 data-[orientation=vertical]:h-[20px]"
                    />
                    <TabsTrigger
                      value="qualified-leads"
                      className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                    >
                      <ClipboardCheck className="mr-2 size-4" />{" "}
                      {t("pages:campaigns.tabs.qualifiedLeads")}
                    </TabsTrigger>
                  </>
                ) : null}
                <Separator
                  orientation="vertical"
                  className="mx-4 data-[orientation=vertical]:h-[20px]"
                />
                {isSourcingCampaign ? (
                  <>
                    <TabsTrigger
                      value="quotes"
                      className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                    >
                      <FileText className="mr-2 size-4" /> {t("pages:campaigns.tabs.quotes")}
                    </TabsTrigger>
                    <Separator
                      orientation="vertical"
                      className="mx-4 data-[orientation=vertical]:h-[20px]"
                    />
                  </>
                ) : null}
                <TabsTrigger
                  value="message"
                  className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                >
                  <Mail className="mr-2 size-4" /> {t("pages:campaigns.tabs.message")}
                </TabsTrigger>
                {isSourcingCampaign ? (
                  <>
                    <Separator
                      orientation="vertical"
                      className="mx-4 data-[orientation=vertical]:h-[20px]"
                    />
                    <TabsTrigger
                      value="todo"
                      className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                    >
                      <FileQuestion className="mr-2 size-4" /> {t("pages:campaigns.tabs.todo")}
                    </TabsTrigger>
                  </>
                ) : null}
                {!isSourcingCampaign ? (
                  <>
                    <Separator
                      orientation="vertical"
                      className="mx-4 data-[orientation=vertical]:h-[20px]"
                    />
                    <TabsTrigger
                      value="tasks"
                      className={`rounded cursor-pointer text-[#7E7E7E] font-normal data-[state=active]:rounded-[8px] data-[state=active]:font-medium data-[state=active]:bg-[#F6F7F8] data-[state=active]:text-gray-900 ${activeUnderlineClass}`}
                    >
                      <FileText className="mr-2 size-4" /> {t("pages:campaigns.tabs.tasks")}
                    </TabsTrigger>
                  </>
                ) : null}
              </TabsList>
              <Separator className="bg-[#dfdfdf]" />

              <TabsContent value="overview" className="p-6 space-y-6">
                <Overview
                  onNavigateTab={handleTabChange}
                  refreshKey={activeTab === "overview" ? tabRefreshKeys.overview : undefined}
                />
              </TabsContent>

              <TabsContent value="activity" className="p-6 space-y-6">
                <ActivityPage
                  refreshKey={activeTab === "activity" ? tabRefreshKeys.activity : undefined}
                />
              </TabsContent>

              {isSourcingCampaign ? (
                <TabsContent value="supplier" className="p-6 space-y-6">
                  <SuppliersPage
                    refreshKey={activeTab === "supplier" ? tabRefreshKeys.supplier : undefined}
                  />
                </TabsContent>
              ) : (
                <TabsContent value="lead" className="p-6 space-y-6">
                  <LeadsPage refreshKey={activeTab === "lead" ? tabRefreshKeys.lead : undefined} />
                </TabsContent>
              )}

              {!isSourcingCampaign ? (
                <TabsContent value="qualified-leads" className="p-6 space-y-6">
                  <LeadsPage
                    initialStatus="qualified"
                    showTabTrigger={false}
                    refreshKey={
                      activeTab === "qualified-leads" ? tabRefreshKeys.qualifiedLeads : undefined
                    }
                  />
                </TabsContent>
              ) : null}

              <TabsContent value="message" className="p-6 space-y-6">
                <MessagePage
                  refreshKey={activeTab === "message" ? tabRefreshKeys.message : undefined}
                />
              </TabsContent>

              {isSourcingCampaign ? (
                <TabsContent value="todo" className="p-6 space-y-6">
                  <SupplierQuestionTodos />
                </TabsContent>
              ) : null}

              {!isSourcingCampaign ? (
                <TabsContent value="tasks" className="p-6 space-y-6">
                  <TasksPage />
                </TabsContent>
              ) : null}

              {isSourcingCampaign ? (
                <TabsContent value="quotes" className="p-6 space-y-6">
                  <QuotesPage />
                </TabsContent>
              ) : null}
            </Tabs>
          </div>
        </div>
      </div>
      {isSourcingCampaign ? (
        <RfqSettingSidebar
          isOpen={showRfqSettingSidebar}
          onClose={() => setShowRfqSettingSidebar(false)}
          campaignId={campaign.id}
          workspaceId={workspaceId}
          campaignName={campaign.name ?? t("pages:campaigns.detail.header.untitled")}
          defaultCountry={resolveRfqDefaultCountryFromLocation(workspaceCompanyLocation)}
          currentUserDisplayName={currentUserDisplayName}
          supplierSearchText={supplierSearchText || rfqSidebarDetails.product}
          supplierCriteria={supplierCriteria}
          supplierCount={supplierSidebarItems.length}
          details={rfqSidebarDetails}
          isConfirming={isRfqCommitting}
          onConfirm={handleRfqConfirm}
          rfqFieldErrors={rfqFieldErrors}
          variant="detail"
          mode="update"
        />
      ) : null}
      {!isSourcingCampaign ? (
        <CampaignSettingsSidebar
          open={showSalesSettingSidebar}
          onOpenChange={setShowSalesSettingSidebar}
          workspaceId={workspaceId}
          workspaceSlug={workspaceSlug}
          campaignId={campaign.id}
          campaignName={campaign.name ?? t("pages:campaigns.detail.header.untitled")}
          initialSetting={null}
          variant="detail"
          salesTargetProfile={defaultSalesTargetProfile}
          currentUserDisplayName={currentUserDisplayName}
          mode="update"
        />
      ) : null}
      <UpgradePlanModal
        open={taskLimitMessage !== null}
        onOpenChange={(open) => {
          if (!open) {
            setTaskLimitMessage(null);
          }
        }}
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        noticeTitle={t("pages:campaigns.paywall.limitTitle")}
        noticeDescription={taskLimitMessage ?? undefined}
      />
    </div>
  );
}
